/**
 * Namespace `app` — строки веб-версии (в отличие от `landing`, который живёт
 * в translations.js как исторический плоский словарь).
 *
 * Источник русских формулировок — ios/Aura/Resources/Aura/LocalizedStrings/.
 * Здесь только те разделы, что уже портированы в веб; растёт по мере фаз.
 */

const ru = {
  common: {
    cancel: 'Отмена',
    save: 'Сохранить',
    delete: 'Удалить',
    close: 'Закрыть',
    retry: 'Повторить',
    loading: 'Загрузка…',
    error: 'Что-то пошло не так',
    empty: 'Пока пусто',
    confirm: 'Подтвердить',
    back: 'Назад',
  },
  auth: {
    signIn: 'Вход в Aura',
    phone: 'Номер телефона',
    codeSent: 'Код отправлен на {{phone}}',
    code: 'Код из SMS',
    continue: 'Продолжить',
    resend: 'Отправить код ещё раз',
    withApple: 'Продолжить с Apple',
    withGoogle: 'Продолжить с Google',
    appleUnavailable: 'Вход через Apple временно недоступен',
    googleUnavailable: 'Вход через Google временно недоступен',
  },
  settings: {
    title: 'Настройки',
    appearance: 'Внешний вид',
    appearanceHint: 'Тема применяется мгновенно и сохраняется в этом браузере.',
    themeSystem: 'Системная',
    themeLight: 'Светлая',
    themeDark: 'Тёмная',
    language: 'Язык',
    languageHint: 'Язык интерфейса веб-версии.',
    notifications: 'Уведомления',
    notificationsPush: 'Push-уведомления в браузере',
    notificationsHintOn: 'Уведомления включены для этого браузера.',
    notificationsHintOff:
      'Разрешите уведомления, чтобы не пропускать сообщения и звонки.',
    notificationsBlocked:
      'Уведомления заблокированы в настройках браузера — разрешите их вручную.',
    notificationsUnsupported: 'Браузер не поддерживает push-уведомления.',
    privacy: 'Приватность',
    blockedUsers: 'Заблокированные пользователи',
    blockedEmpty: 'Вы никого не заблокировали',
    unblock: 'Разблокировать',
    account: 'Аккаунт',
    verification: 'Верификация профиля',
    legal: 'Документы',
    userAgreement: 'Пользовательское соглашение',
    privacyPolicy: 'Политика конфиденциальности',
    cookiePolicy: 'Политика cookie',
    signOut: 'Выйти из аккаунта',
    deleteAccount: 'Удалить аккаунт',
    deleteAccountTitle: 'Удалить аккаунт?',
    deleteAccountText:
      'Профиль, публикации, переписки и баланс будут удалены безвозвратно. Это действие нельзя отменить.',
    deleteAccountConfirm: 'Введите «УДАЛИТЬ», чтобы подтвердить',
    deleteAccountKeyword: 'УДАЛИТЬ',
  },
  kyc: {
    title: 'Верификация профиля',
    subtitle: 'Подтвердите, что вы — это вы. Занимает пару минут.',
    statusNone: 'Не пройдена',
    statusPending: 'На проверке',
    statusApproved: 'Профиль подтверждён',
    statusRejected: 'Отклонена',
    start: 'Пройти верификацию',
    unavailable: 'Верификация временно недоступна',
  },
};

/** Русский словарь — эталон структуры: en/az обязаны иметь те же ключи. */
export type AppNamespace = typeof ru;

const en: AppNamespace = {
  common: {
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    close: 'Close',
    retry: 'Retry',
    loading: 'Loading…',
    error: 'Something went wrong',
    empty: 'Nothing here yet',
    confirm: 'Confirm',
    back: 'Back',
  },
  auth: {
    signIn: 'Sign in to Aura',
    phone: 'Phone number',
    codeSent: 'Code sent to {{phone}}',
    code: 'SMS code',
    continue: 'Continue',
    resend: 'Send the code again',
    withApple: 'Continue with Apple',
    withGoogle: 'Continue with Google',
    appleUnavailable: 'Apple sign-in is temporarily unavailable',
    googleUnavailable: 'Google sign-in is temporarily unavailable',
  },
  settings: {
    title: 'Settings',
    appearance: 'Appearance',
    appearanceHint: 'The theme applies instantly and is saved in this browser.',
    themeSystem: 'System',
    themeLight: 'Light',
    themeDark: 'Dark',
    language: 'Language',
    languageHint: 'Interface language of the web version.',
    notifications: 'Notifications',
    notificationsPush: 'Browser push notifications',
    notificationsHintOn: 'Notifications are enabled for this browser.',
    notificationsHintOff: 'Allow notifications so you never miss messages and calls.',
    notificationsBlocked:
      'Notifications are blocked in your browser settings — allow them manually.',
    notificationsUnsupported: 'This browser does not support push notifications.',
    privacy: 'Privacy',
    blockedUsers: 'Blocked users',
    blockedEmpty: 'You have not blocked anyone',
    unblock: 'Unblock',
    account: 'Account',
    verification: 'Profile verification',
    legal: 'Legal',
    userAgreement: 'Terms of use',
    privacyPolicy: 'Privacy policy',
    cookiePolicy: 'Cookie policy',
    signOut: 'Sign out',
    deleteAccount: 'Delete account',
    deleteAccountTitle: 'Delete account?',
    deleteAccountText:
      'Your profile, posts, chats and balance will be deleted permanently. This cannot be undone.',
    deleteAccountConfirm: 'Type “DELETE” to confirm',
    deleteAccountKeyword: 'DELETE',
  },
  kyc: {
    title: 'Profile verification',
    subtitle: 'Confirm that you are you. It takes a couple of minutes.',
    statusNone: 'Not verified',
    statusPending: 'Under review',
    statusApproved: 'Profile verified',
    statusRejected: 'Rejected',
    start: 'Start verification',
    unavailable: 'Verification is temporarily unavailable',
  },
};

const az: AppNamespace = {
  common: {
    cancel: 'Ləğv et',
    save: 'Yadda saxla',
    delete: 'Sil',
    close: 'Bağla',
    retry: 'Yenidən cəhd et',
    loading: 'Yüklənir…',
    error: 'Nəsə səhv getdi',
    empty: 'Hələ boşdur',
    confirm: 'Təsdiqlə',
    back: 'Geri',
  },
  auth: {
    signIn: 'Aura-ya daxil ol',
    phone: 'Telefon nömrəsi',
    codeSent: 'Kod {{phone}} nömrəsinə göndərildi',
    code: 'SMS kodu',
    continue: 'Davam et',
    resend: 'Kodu yenidən göndər',
    withApple: 'Apple ilə davam et',
    withGoogle: 'Google ilə davam et',
    appleUnavailable: 'Apple ilə giriş müvəqqəti əlçatmazdır',
    googleUnavailable: 'Google ilə giriş müvəqqəti əlçatmazdır',
  },
  settings: {
    title: 'Tənzimləmələr',
    appearance: 'Görünüş',
    appearanceHint: 'Tema dərhal tətbiq olunur və bu brauzerdə saxlanılır.',
    themeSystem: 'Sistem',
    themeLight: 'İşıqlı',
    themeDark: 'Qaranlıq',
    language: 'Dil',
    languageHint: 'Veb versiyanın interfeys dili.',
    notifications: 'Bildirişlər',
    notificationsPush: 'Brauzer push bildirişləri',
    notificationsHintOn: 'Bildirişlər bu brauzer üçün aktivdir.',
    notificationsHintOff: 'Mesaj və zəngləri qaçırmamaq üçün bildirişlərə icazə verin.',
    notificationsBlocked:
      'Bildirişlər brauzer tənzimləmələrində bloklanıb — əl ilə icazə verin.',
    notificationsUnsupported: 'Brauzer push bildirişləri dəstəkləmir.',
    privacy: 'Məxfilik',
    blockedUsers: 'Bloklanmış istifadəçilər',
    blockedEmpty: 'Heç kimi bloklamamısınız',
    unblock: 'Blokdan çıxar',
    account: 'Hesab',
    verification: 'Profil təsdiqi',
    legal: 'Sənədlər',
    userAgreement: 'İstifadəçi razılaşması',
    privacyPolicy: 'Məxfilik siyasəti',
    cookiePolicy: 'Cookie siyasəti',
    signOut: 'Hesabdan çıx',
    deleteAccount: 'Hesabı sil',
    deleteAccountTitle: 'Hesab silinsin?',
    deleteAccountText:
      'Profil, paylaşımlar, yazışmalar və balans birdəfəlik silinəcək. Bunu geri qaytarmaq mümkün deyil.',
    deleteAccountConfirm: 'Təsdiq üçün «SİL» yazın',
    deleteAccountKeyword: 'SİL',
  },
  kyc: {
    title: 'Profil təsdiqi',
    subtitle: 'Özünüz olduğunuzu təsdiqləyin. Bir neçə dəqiqə çəkir.',
    statusNone: 'Keçilməyib',
    statusPending: 'Yoxlanılır',
    statusApproved: 'Profil təsdiqlənib',
    statusRejected: 'Rədd edilib',
    start: 'Təsdiqdən keç',
    unavailable: 'Təsdiq müvəqqəti əlçatmazdır',
  },
};

export const appResources: Record<string, AppNamespace> = { ru, en, az };
